import Vue from "vue";
import VueRouter from "vue-router";

import { TTRouteConfig, TTRouteAccessLevel } from "@tt-app/models";
import { isUserAuthenticated } from "@tt-app/utils";
import { MainLogger } from "@tt-app/utils/internal/logger";

import Dashboard from "@tt-app/views/Dashboard.vue";
import Error404 from "@tt-app/views/404.vue";

// ----- Routes configuration
import { ROUTES } from "@tt-app/constants/router";
import { assignmentRoutes } from "./assignment";
import { courseRoutes } from "./course";
import { exerciseRoutes } from "./exercise";
import { learningHistoryRoutes } from "./learningHistory";
import { userRoutes } from "./user";
import store from "@tt-app/store";
import { VUEX_ROOT_ACTION_NAMES } from "@tt-app/constants";
import { skillsRoutes } from "@tt-app/router/skills";
import { terratalkLiveRoutes } from "@tt-app/router/terratalkLive";
import { TERRATALK_LIVE_ROUTES } from "@tt-app/constants/router/terratalkLive";
import {
  TerratalkLiveGetterNames,
  TerratalkLiveNamespace
} from "@tt-app/store/terratalkLive/types";
import { releaseNotesRoutes } from "@tt-app/router/releaseNotes";

export const allRoutes: TTRouteConfig[] = [
  {
    path: ROUTES.HOME.PATH,
    name: ROUTES.HOME.NAME,
    accessLevel: "authenticated",
    type: "page",
    component: Dashboard
  },
  ...assignmentRoutes,
  ...courseRoutes,
  ...exerciseRoutes,
  ...learningHistoryRoutes,
  ...skillsRoutes,
  ...terratalkLiveRoutes,
  ...userRoutes,
  ...releaseNotesRoutes,
  // Catch-them-all in last position
  {
    path: ROUTES.NOT_FOUND.PATH,
    name: ROUTES.NOT_FOUND.NAME,
    accessLevel: "public",
    type: "page",
    component: Error404
  }
];

const getRouteNameForAccess = (
  allRoutes: TTRouteConfig[],
  accessLevel: TTRouteAccessLevel
): string[] => {
  return (
    allRoutes
      .filter(routeCfg => routeCfg.accessLevel === accessLevel)
      .map(routeCfg => routeCfg.name)
      // God bless StackOverflow: https://codereview.stackexchange.com/a/184004
      .filter((name: string | undefined): name is string => !!name)
  );
};

export const PUBLIC_ROUTE_NAMES: string[] = getRouteNameForAccess(allRoutes, "public");
export const PRIVATE_ROUTE_NAMES: string[] = getRouteNameForAccess(allRoutes, "authenticated");
export const EXERCISES_ROUTE_NAMES: string[] = allRoutes
  .filter(routeCfg => routeCfg.type === "exercise")
  .map(cfg => cfg.name as string);
export const USER_ROUTE_NAMES: string[] = userRoutes.map(route => {
  return route.name as string;
});
export const LIVE_SESSION_ALLOWED_ROUTE_NAMES: string[] = [
  TERRATALK_LIVE_ROUTES.LOBBY.NAME,
  ...EXERCISES_ROUTE_NAMES,
  ...USER_ROUTE_NAMES
];

// ----- VueRouter config
Vue.use(VueRouter);

const router = new VueRouter({
  routes: allRoutes
});

// global guards
router.beforeEach((to, from, next): void => {
  // ensure that destination route name is not undefined
  const destRouteName = to.name || "undefined-path-name";
  const destRoutePath = to.path || "/";

  // Off we go!
  if (PUBLIC_ROUTE_NAMES.includes(destRouteName)) {
    next();
    return;
  }

  // Check authentication
  if (PRIVATE_ROUTE_NAMES.includes(destRouteName)) {
    if (isUserAuthenticated()) {
      const limitedModeDetails =
        store.getters[`${TerratalkLiveNamespace}/${TerratalkLiveGetterNames.LIMITED_MODE_DETAILS}`];
      const routeAllowedDuringLimitedMode = LIVE_SESSION_ALLOWED_ROUTE_NAMES.includes(
        to.name || ""
      );
      const goingToLobby = to.name === TERRATALK_LIVE_ROUTES.LOBBY.NAME;

      if (limitedModeDetails && !routeAllowedDuringLimitedMode && !goingToLobby) {
        next({
          name: ROUTES.TERRATALK_LIVE.LOBBY.NAME
        });
        return;
      }

      next();
    } else {
      MainLogger.info(`Access to ${destRoutePath} requires being logged in`);
      next({
        name: ROUTES.USER.AUTHENTICATION.LOGIN.NAME,
        query: { nextPath: destRoutePath },
        // Use replace: true to avoid "pushing" the login page in the History
        replace: true
      });
    }
  }

  // Save previous path if applicable
  store.dispatch(VUEX_ROOT_ACTION_NAMES.SAVE_PREVIOUS_ROUTE, from);

  next();
});

export default router;
